<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  ChevronDown,
  Building,
  Settings,
  Save
} from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import { useStore } from 'vuex'

const props = defineProps<{
  organizations: any[]
  loading: boolean
  defaultFeatureFlags: Record<string, boolean>
}>()

const emit = defineEmits<{
  (e: 'organization-updated'): void
}>()

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const expandedRows = ref(new Set<number>())
const savingFeatures = ref(new Set<number>())

// Store local feature states for each organization
const localFeatures = ref<Record<number, Record<string, boolean>>>({})

const featureConfigs = [
  {
    key: 'static',
    label: 'Static',
    description: 'Static data layers'
  },
  {
    key: 'collected_data',
    label: 'Collected Data',
    description: 'EarthPBC collected data'
  },
  {
    key: 'planet_order',
    label: 'Order Scene',
    description: 'Planet Labs scene ordering'
  },
  {
    key: 'planet_vars',
    label: 'Planetary Variables',
    description: 'Planet Labs variables'
  },
  {
    key: 'radar',
    label: 'Radar',
    description: 'Protected Seas radar data'
  }
]

// Default feature values
const DEFAULT_FEATURE_VALUES = {
  static: true,
  collected_data: true,
  radar: false,
  planet_vars: false,
  planet_order: false
}

// Initialize local features when organizations change
const initializeLocalFeatures = () => {
  props.organizations.forEach(org => {
    if (!localFeatures.value[org.organization_id]) {
      localFeatures.value[org.organization_id] = {
        ...DEFAULT_FEATURE_VALUES,
        ...org.features
      }
    }
  })
}

// Watch for changes in organizations prop
watch(() => props.organizations, () => {
  initializeLocalFeatures()
}, { immediate: true })

const toggleRowExpansion = (orgId: number) => {
  if (expandedRows.value.has(orgId)) {
    expandedRows.value.delete(orgId)
  } else {
    expandedRows.value.add(orgId)
    // Initialize local features for this org if not already done
    const org = props.organizations.find(o => o.organization_id === orgId)
    if (org && !localFeatures.value[orgId]) {
      localFeatures.value[orgId] = {
        ...DEFAULT_FEATURE_VALUES,
        ...org.features
      }
    }
  }
  expandedRows.value = new Set(expandedRows.value) // Force reactivity
}

const updateFeature = (orgId: number, featureKey: string, value: boolean) => {
  if (!localFeatures.value[orgId]) {
    localFeatures.value[orgId] = {}
  }
  localFeatures.value[orgId][featureKey] = value
}

const getFeatureValue = (orgId: number, featureKey: string): boolean => {
  // First check local state
  if (localFeatures.value[orgId] && localFeatures.value[orgId][featureKey] !== undefined) {
    return localFeatures.value[orgId][featureKey]
  }

  // Then check org features
  const org = props.organizations.find(o => o.organization_id === orgId)
  if (org?.features && org.features[featureKey] !== undefined) {
    return org.features[featureKey]
  }

  // Use default values from props
  return props.defaultFeatureFlags[featureKey] ?? false
}

const saveSettings = async (org: any) => {
  savingFeatures.value.add(org.organization_id)

  try {
    // Save features
    const featuresData = localFeatures.value[org.organization_id] || DEFAULT_FEATURE_VALUES

    const response = await API({
      method: 'PATCH',
      url: `${HOST}/admin/organizations/${org.organization_id}/features`,
      data: featuresData
    })

    // Update the org with the response data
    if (response.data.features) {
      org.features = response.data.features
      // Update local state to match
      localFeatures.value[org.organization_id] = { ...response.data.features }
    }

    toast({
      title: 'Success',
      description: `Data source features updated for ${org.organization_name}`,
    })

    emit('organization-updated')
  } catch (error) {
    console.error('Error saving features:', error)
    toast({
      title: 'Error',
      description: `Failed to update features: ${error instanceof Error ? error.message : String(error)}`,
      variant: 'destructive'
    })
  } finally {
    savingFeatures.value.delete(org.organization_id)
  }
}

const getPlanBadgeVariant = (plan: string) => {
  switch(plan?.toLowerCase()) {
    case 'unlimited':
      return 'default'
    case 'basic':
      return 'secondary'
    default:
      return 'outline'
  }
}
</script>

<template>
  <div class="h-full">
    <div v-if="loading" class="p-4">
      <div class="space-y-3">
        <div v-for="i in 5" :key="i" class="flex items-center space-x-3 animate-pulse">
          <div class="h-10 w-10 bg-gray-200 rounded"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom table implementation with expandable rows -->
    <div v-else class="border rounded-md">
      <table class="w-full">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              <div class="flex items-center gap-2">
                <Building class="h-4 w-4" />
                Organization
              </div>
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Plan
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Created
            </th>
            <th class="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="org in organizations" :key="org.organization_id">
            <!-- Main row -->
            <tr class="border-b hover:bg-muted/50">
              <td class="p-4 align-middle">
                <div>
                  <div class="font-medium">{{ org.organization_name }}</div>
                  <div class="text-sm text-gray-500">ID: {{ org.organization_id }}</div>
                </div>
              </td>
              <td class="p-4 align-middle">
                <Badge :variant="getPlanBadgeVariant(org.current_plan)">
                  {{ org.current_plan || 'Free' }}
                </Badge>
              </td>
              <td class="p-4 align-middle">
                <div class="text-gray-500 text-sm whitespace-nowrap">
                  {{ formatTime(org.create_dttm, settings?.tz || 'UTC', 'D MMM YYYY') }}
                </div>
              </td>
              <td class="p-4 align-middle">
                <Button
                  variant="ghost"
                  size="sm"
                  @click="toggleRowExpansion(org.organization_id)"
                  class="gap-1"
                >
                  <Settings class="h-4 w-4" />
                  Settings
                  <ChevronDown
                    class="h-4 w-4 transition-transform duration-200"
                    :class="{ 'rotate-180': expandedRows.has(org.organization_id) }"
                  />
                </Button>
              </td>
            </tr>

            <!-- Expanded row for settings -->
            <tr v-if="expandedRows.has(org.organization_id)" class="border-b bg-gray-50">
              <td colspan="4" class="p-0">
                <div class="px-8 py-6">
                  <h3 class="font-semibold text-lg mb-4">Features</h3>

                  <div class="space-y-3 mb-6">
                    <div
                      v-for="feature in featureConfigs"
                      :key="feature.key"
                      class="flex items-center justify-between p-3 bg-white rounded-lg border"
                    >
                      <div>
                        <Label :for="`${org.organization_id}-${feature.key}`" class="font-medium">
                          {{ feature.label }}
                        </Label>
                        <p class="text-sm text-gray-500 mt-1">{{ feature.description }}</p>
                      </div>
                      <Switch
                        :id="`${org.organization_id}-${feature.key}`"
                        :model-value="getFeatureValue(org.organization_id, feature.key)"
                        @update:model-value="(value) => updateFeature(org.organization_id, feature.key, value)"
                      />
                    </div>
                  </div>

                  <div class="flex justify-end">
                    <Button
                      @click="saveSettings(org)"
                      :disabled="savingFeatures.has(org.organization_id)"
                      class="gap-2"
                    >
                      <Save class="h-4 w-4" />
                      {{ savingFeatures.has(org.organization_id) ? 'Saving...' : 'Save Settings' }}
                    </Button>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>